/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Typography, Input, Select, Spin, message, Modal } from 'antd';
import { ClockCircleOutlined, PlusOutlined, DeleteOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { Clock, Plus, Trash2, AlertTriangle, Save, CornerDownRight, X, ChevronDown, ChevronUp, Search } from 'lucide-react';
import { Button, Header, Checkbox, ConfirmationModal, Spinner } from '@crc-evans/ui-components';

import axiosConfig from '../../services/axiosConfig';
import { useDispatch, useSelector } from 'react-redux';
import { updateDprSkipState } from '../../redux/dpr/action';
import moment from 'moment';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// Custom Multi-Select Component for Options (with checkboxes)
const CustomOptionMultiSelect = ({ value, options, onChange, placeholder = "Select Options...", disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const selectedValues = Array.isArray(value) ? value : (value ? [value] : []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleValue = (val) => {
    let newValues;
    if (selectedValues.includes(val)) {
      newValues = selectedValues.filter(v => v !== val);
    } else {
      newValues = [...selectedValues, val];
    }
    onChange(newValues);
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', textAlign: 'left' }}>
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          minHeight: '38px',
          height: '38px',
          boxSizing: 'border-box',
          padding: '4px 12px',
          borderRadius: '6px',
          border: isOpen ? '2px solid #3B82F6' : '1px solid #E5E7EB',
          backgroundColor: disabled ? '#F9FAFB' : '#fff',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '8px',
          transition: 'all 0.2s',
          opacity: disabled ? 0.6 : 1,
          overflow: 'hidden',
          minWidth: 0
        }}
      >
        <div style={{
          display: 'flex',
          flexWrap: 'nowrap',
          gap: '6px',
          flex: 1,
          minWidth: 0,
          overflowX: 'auto',
          overflowY: 'hidden',
          alignItems: 'center',
          scrollbarWidth: 'thin'
        }}>
          {selectedValues.length > 0 ? (
            selectedValues.map(val => (
              <span
                key={val}
                style={{
                  backgroundColor: '#EFF6FF',
                  color: '#1E40AF',
                  fontSize: '11px',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  border: '1px solid #DBEAFE',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  flexShrink: 0,
                  maxWidth: '180px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{val}</span>
                <X
                  size={10}
                  style={{ cursor: 'pointer', flexShrink: 0 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleValue(val);
                  }}
                />
              </span>
            ))
          ) : (
            <span style={{ color: '#9CA3AF', fontSize: '14px' }}>{placeholder}</span>
          )}
        </div>
        <svg width="12" height="12" viewBox="0 0 12 12" style={{ flexShrink: 0 }}>
          <path fill="#6B7280" d="M6 9L1 4h10z" />
        </svg>
      </div>

      {isOpen && !disabled && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          left: 0,
          right: 0,
          zIndex: 1000,
          backgroundColor: '#fff',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          padding: '8px',
          maxHeight: '200px',
          overflowY: 'auto'
        }}>
          {options && options.length > 0 ? (
            options.map((opt, index) => (
              <div
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleValue(opt);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 10px',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  marginBottom: '2px',
                  backgroundColor: selectedValues.includes(opt) ? '#F3F4F6' : 'transparent'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = selectedValues.includes(opt) ? '#F3F4F6' : 'transparent'}
              >
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: selectedValues.includes(opt) ? 'none' : '1px solid #D1D5DB',
                  borderRadius: '3px',
                  backgroundColor: selectedValues.includes(opt) ? '#3B82F6' : 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '10px',
                  flexShrink: 0
                }}>
                  {selectedValues.includes(opt) && (
                    <svg width="10" height="10" viewBox="0 0 10 10">
                      <path fill="white" d="M8.5 2.5L4 7L1.5 4.5L2.2 3.8L4 5.6L7.8 1.8L8.5 2.5Z" />
                    </svg>
                  )}
                </div>
                <span style={{
                  fontSize: '13px',
                  color: '#374151',
                  fontWeight: selectedValues.includes(opt) ? 500 : 400
                }}>
                  {opt}
                </span>
              </div>
            ))
          ) : (
            <div style={{ padding: '12px', textAlign: 'center', color: '#9CA3AF', fontSize: '13px' }}>
              No options available
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Single-select dropdown – same styling as Option dropdown (Category / Subcategory)
const CustomSingleSelect = ({ value, options = [], onChange, placeholder = 'Select...', disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const opts = Array.isArray(options) ? options : [];
  const displayLabel = value || '';

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', minWidth: 0, textAlign: 'left' }}>
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          width: '100%',
          minHeight: '38px',
          height: '38px',
          boxSizing: 'border-box',
          padding: '4px 12px',
          fontSize: '13px',
          border: isOpen ? '2px solid #3B82F6' : '1px solid #E5E7EB',
          borderRadius: '6px',
          backgroundColor: disabled ? '#F9FAFB' : '#fff',
          color: displayLabel ? '#374151' : '#9CA3AF',
          outline: 'none',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          transition: 'all 0.2s',
          opacity: disabled ? 0.6 : 1,
          overflow: 'hidden'
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {displayLabel || placeholder}
        </span>
        {isOpen ? <ChevronUp size={16} color="#6B7280" /> : <ChevronDown size={16} color="#6B7280" />}
      </div>

      {isOpen && !disabled && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          left: 0,
          right: 0,
          zIndex: 9999,
          backgroundColor: '#fff',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          padding: '8px',
          maxHeight: '200px',
          overflowY: 'auto',
          overflowX: 'hidden'
        }}
        >
          {opts.map((opt) => {
            const optValue = typeof opt === 'string' ? opt : (opt.value ?? opt.name ?? opt.label ?? '');
            const optLabel = typeof opt === 'string' ? opt : (opt.label ?? opt.name ?? opt.value ?? '');
            return (
              <div
                key={optValue}
                onClick={() => { onChange(optValue); setIsOpen(false); }}
                style={{
                  padding: '8px 10px',
                  fontSize: '13px',
                  color: '#374151',
                  backgroundColor: value === optValue ? '#F3F4F6' : 'transparent',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  marginBottom: '2px',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = value === optValue ? '#F3F4F6' : 'transparent'}
              >
                {optLabel}
              </div>
            );
          })}
          {opts.length === 0 && (
            <div style={{ padding: '8px 10px', fontSize: '13px', color: '#9CA3AF' }}>No options</div>
          )}
        </div>
      )}
    </div>
  );
};

// Initial Downtime Reasons Configuration
const INITIAL_DOWNTIME_REASONS = [
  {
    "code": "WELDING_MACHINE_BREAKDOWN",
    "label": "Welding Machine Breakdown",
    "description": "Welding Machine Breakdown"
  },
  {
    "code": "POWER_SUPPLY_FAILURE",
    "label": "Power Supply Failure (Generator / Electrical Issue)",
    "description": "Power Supply Failure (Generator / Electrical Issue)"
  },
  {
    "code": "ELECTRODE_CONSUMABLE_SHORTAGE",
    "label": "Electrode / Welding Consumable Shortage",
    "description": "Electrode / Welding Consumable Shortage"
  },
  {
    "code": "WELDER_UNAVAILABILITY",
    "label": "Welder Unavailability (Manpower Issue)",
    "description": "Welder Unavailability (Manpower Issue)"
  },
  {
    "code": "JOINT_PREPARATION_DELAY",
    "label": "Joint Preparation Delay (Fit-up / Alignment Issue)",
    "description": "Joint Preparation Delay (Fit-up / Alignment Issue)"
  },
  {
    "code": "WEATHER_CONDITIONS",
    "label": "Weather Conditions (Rain / Wind / High Humidity)",
    "description": "Weather Conditions (Rain / Wind / High Humidity)"
  },
  {
    "code": "INSPECTION_HOLD_QC_PENDING",
    "label": "Inspection Hold / QC Clearance Pending",
    "description": "Inspection Hold / QC Clearance Pending"
  },
  {
    "code": "REWELDING_DEFECT_REPAIR",
    "label": "Re-welding Due to Defect / Repair Work",
    "description": "Re-welding Due to Defect / Repair Work"
  },
  {
    "code": "SAFETY_HOLD_PERMIT",
    "label": "Safety Hold / Permit Not Available",
    "description": "Safety Hold / Permit Not Available"
  },
  {
    "code": "OTHERS",
    "label": "Others",
    "description": "Others"
  }
];

// Custom Reason Select - dropdown with "Add new reason" input inside (like Personnel Add new role)
const CustomReasonSelect = ({ value, options, onChange, onAddReason, placeholder = 'Select Reason...' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [newReasonInput, setNewReasonInput] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const containerRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setIsAdding(false);
      setSearchTerm('');
      setNewReasonInput('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [isOpen, options?.length, isAdding]);

  const filteredOptions = (options || []).filter(opt =>
    (opt.label || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = (e) => {
    e.stopPropagation();
    if (newReasonInput.trim()) {
      onAddReason(newReasonInput.trim());
      setNewReasonInput('');
      setIsAdding(false);
      setIsOpen(false);
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', textAlign: 'left' }}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          padding: '10px 12px',
          fontSize: '14px',
          border: '1px solid #E5E7EB',
          borderRadius: '6px',
          backgroundColor: '#FFFFFF',
          color: value ? '#1F2937' : '#9CA3AF',
          outline: 'none',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxSizing: 'border-box'
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value || placeholder}
        </span>
        {isOpen ? <ChevronUp size={16} color="#6B7280" /> : <ChevronDown size={16} color="#6B7280" />}
      </div>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          left: 0,
          right: 0,
          zIndex: 1000,
          backgroundColor: '#FFFFFF',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* Search Bar */}
          <div style={{ padding: '8px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Search size={14} color="#9CA3AF" />
            <input
              autoFocus
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              placeholder="Search reasons..."
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                fontSize: '13px',
                color: '#1F2937'
              }}
            />
          </div>

          {/* List */}
          <div
            ref={listRef}
            style={{
              maxHeight: '200px',
              overflowY: 'auto',
              padding: '4px 0'
            }}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => (
                <div
                  key={opt.code}
                  onClick={() => {
                    onChange(opt.label);
                    setIsOpen(false);
                  }}
                  style={{
                    padding: '8px 12px',
                    fontSize: '14px',
                    color: '#374151',
                    cursor: 'pointer',
                    backgroundColor: value === opt.label ? '#F3F4F6' : 'transparent',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = value === opt.label ? '#F3F4F6' : 'transparent'}
                >
                  {opt.label}
                </div>
              ))
            ) : (
              <div style={{ padding: '12px', fontSize: '13px', color: '#9CA3AF', textAlign: 'center' }}>
                No reasons found
              </div>
            )}
            <div
              onClick={(e) => {
                e.stopPropagation();
                setIsAdding(true);
              }}
              style={{
                padding: '8px 12px',
                fontSize: '14px',
                color: '#3B82F6',
                fontWeight: 600,
                cursor: 'pointer',
                borderTop: '1px solid #F3F4F6'
              }}
            >
              + Add new reason
            </div>
          </div>

          {/* Footer: Add Reason Input - inside dropdown */}
          {isAdding && (
            <div style={{
              padding: '8px',
              borderTop: '1px solid #F3F4F6',
              backgroundColor: '#F9FAFB',
              display: 'flex',
              gap: '8px',
              alignItems: 'center'
            }}>
              <input
                autoFocus
                value={newReasonInput}
                onChange={(e) => setNewReasonInput(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.key === 'Enter' && handleAdd(e)}
                placeholder="Enter new reason..."
                style={{
                  flex: 1,
                  padding: '6px 10px',
                  fontSize: '13px',
                  borderRadius: '4px',
                  border: '1px solid #D1D5DB',
                  outline: 'none',
                  color: '#1F2937',
                  backgroundColor: '#FFFFFF'
                }}
              />
              <div style={{ display: 'flex', gap: '4px' }}>
                <button
                  onClick={handleAdd}
                  disabled={!newReasonInput.trim()}
                  style={{
                    padding: '6px 12px',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#FFFFFF',
                    backgroundColor: newReasonInput.trim() ? '#3B82F6' : '#93C5FD',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: newReasonInput.trim() ? 'pointer' : 'default'
                  }}
                >
                  Add
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsAdding(false);
                    setNewReasonInput('');
                  }}
                  style={{
                    padding: '6px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#6B7280',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Local Component: CommentBox (Copied from ConsumablesLog.js for consistency)
const CommentBox = ({ header, value, onChange, placeholder }) => (
  <div style={{ marginTop: '40px' }}>
    <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 500, color: '#64748B', textAlign: 'left' }}>
      {header}
    </h4>
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={6}
      style={{
        width: '100%',
        padding: '16px',
        fontSize: '15px',
        border: '1px solid #E2E8F0',
        borderRadius: '12px',
        resize: 'vertical',
        outline: 'none',
        fontFamily: 'inherit',
        color: '#1F2937',
        backgroundColor: '#FFFFFF'
      }}
    />
  </div>
);

const DowntimeLog = ({
  downtimeData: initialDowntimeData = {},
  onSave,
  isSubmitting = false,
  shift = 'day',
  dprLogId,
  sowId
}) => {
  const [downtimeRows, setDowntimeRows] = useState([]);
  const [remarks, setRemarks] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [totalDowntimeToday, setTotalDowntimeToday] = useState(0);
  const [reportNo, setReportNo] = useState('');
  const dispatch = useDispatch();
  const skipDowntime = useSelector(state => {
    if (!sowId) return false;
    return state.dprReducer?.skipStatesBySection?.[sowId]?.skipDowntime || false;
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [newCardId, setNewCardId] = useState(null);
  const cardRefs = useRef({});

  // Dynamic Reasons State
  const [availableReasons, setAvailableReasons] = useState(INITIAL_DOWNTIME_REASONS);

  // Delete Modal State
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [rowToDeleteId, setRowToDeleteId] = useState(null);
  const [isSkipConfirmOpen, setIsSkipConfirmOpen] = useState(false);
  const [responsibilityOptions, setResponsibilityOptions] = useState([]);

  const handleSkipDowntimeChange = (checked) => {
    if (!sowId) return;
    if (checked) {
      setIsSkipConfirmOpen(true);
    } else {
      dispatch(updateDprSkipState(sowId, 'skipDowntime', false));
    }
  };

  const handleConfirmSkipDowntime = () => {
    if (sowId) dispatch(updateDprSkipState(sowId, 'skipDowntime', true));
    setIsSkipConfirmOpen(false);
  };

  // Helper function to round hours to 1 decimal place
  const roundHours = (hours) => {
    return Math.round(hours * 10) / 10;
  };

  // Helper function to convert Date to "HH:mm" format for native time input
  const formatTimeForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Helper function to parse "HH:mm" time string to Date object
  const parseTimeFromInput = (timeString) => {
    if (!timeString) return null;
    const [hours, minutes] = timeString.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  };

  // Fetch downtime data from API when shift changes
  const fetchDowntimeData = useCallback(async () => {
    if (!dprLogId) return; // Don't fetch if dprLogId is not available
    setIsLoading(true);
    try {
      const response = await axiosConfig.get(`/dpr-downtime?dprLogId=${dprLogId}&shift=${shift}`);

      console.log('Downtime API Response:', response);
      console.log('Response data:', response.data);

      let downtimeDetailsData = null;
      let notesData = null;
      let reportNoData = null;
      let totalDowntimeTodayData = null;

      if (response.data && response.data.data) {
        downtimeDetailsData = response.data.data.downtimeDetails;
        notesData = response.data.data.notes;
        reportNoData = response.data.data.reportNo;
        totalDowntimeTodayData = response.data.data.totalDowntimeToday;
      }

      // Store reportNo
      if (reportNoData) {
        setReportNo(reportNoData);
      }

      // Store totalDowntimeToday
      if (totalDowntimeTodayData !== null && totalDowntimeTodayData !== undefined) {
        setTotalDowntimeToday(totalDowntimeTodayData);
      }

      // Map downtimeDetails to downtimeRows format
      if (downtimeDetailsData && Array.isArray(downtimeDetailsData) && downtimeDetailsData.length > 0) {
        const mappedRows = downtimeDetailsData.map((detail, index) => {
          // Parse startTime and endTime to Date objects for TimePicker
          let timeFrom = null;
          let timeTo = null;

          if (detail.startTime) {
            const [hours, minutes] = detail.startTime.split(':');
            timeFrom = new Date();
            timeFrom.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
          }

          if (detail.endTime) {
            const [hours, minutes] = detail.endTime.split(':');
            timeTo = new Date();
            timeTo.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
          }

          // Map allocations to responsibilities
          const responsibilities = (detail.allocations || []).map((allocation, allocIndex) => ({
            id: allocation.id || `alloc-${index}-${allocIndex}-${Date.now()}`,
            category: allocation.category || '',
            subCategory: allocation.subCategory || '',
            option: Array.isArray(allocation.option) ? allocation.option : [],
            hours: allocation.hours || 0,
            originalAllocation: allocation // Keep original for reference
          }));

          return {
            id: `downtime-${index}-${detail.id || Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
            reason: detail.categoryTitle || '', // categoryTitle as reason/description
            notes: detail.reason || '', // reason from API in notes field
            timeFrom: timeFrom,
            timeTo: timeTo,
            totalDuration: detail.totalHours || 0,
            responsibilities: responsibilities, // Map allocations to responsibilities
            originalDetail: detail // Keep original for reference
          };
        });

        console.log('Mapped downtime rows:', mappedRows);
        setDowntimeRows(mappedRows);
      } else {
        setDowntimeRows([]);
      }

      // Set notes/remarks
      if (notesData) {
        setRemarks(notesData);
      } else {
        setRemarks('');
      }
    } catch (error) {
      console.error('Error fetching downtime data:', error);
      setDowntimeRows([]);
      setRemarks('');
    } finally {
      setIsLoading(false);
    }
  }, [shift, dprLogId]);

  // Normalize a single category item per API spec: category, subCategory[].name, subCategory[].Options
  const normalizeCategoryItem = (item) => {
    if (item == null) return null;
    if (typeof item === 'string') {
      return { name: item, category: item, categoryName: item, subCategory: [] };
    }
    const name =
      item.category ??
      item.Category ??
      item.name ??
      item.categoryName ??
      item.categoryTitle ??
      item.title ??
      item.label ??
      '';
    if (!name || String(name).trim() === '') return null;
    const subCategoryRaw = Array.isArray(item.subCategory)
      ? item.subCategory
      : Array.isArray(item.SubCategory)
        ? item.SubCategory
        : Array.isArray(item.subCategories)
          ? item.subCategories
          : Array.isArray(item.children)
            ? item.children
            : [];
    const subCategory = subCategoryRaw.map((sub) => {
      if (typeof sub === 'string') {
        return { name: sub, subCategoryName: sub, Options: [], options: [] };
      }
      const subName = sub.name ?? sub.Name ?? sub.subCategoryName ?? sub.title ?? (typeof sub === 'string' ? sub : '');
      const rawOpts = Array.isArray(sub.Options) ? sub.Options : Array.isArray(sub.options) ? sub.options : [];
      const opts = rawOpts.map((o) => (typeof o === 'string' ? o : (o?.name ?? o?.label ?? o?.value ?? String(o))));
      return {
        ...sub,
        name: subName,
        subCategoryName: subName,
        Options: opts,
        options: opts
      };
    });
    return {
      ...item,
      name,
      category: name,
      categoryName: name,
      subCategory
    };
  };

  // Parse raw API response: API returns { message, data: { count, rows: [{ category, subCategory: [{ name, Options }] }, ...] } }
  const parseResponsibilityResponse = (data) => {
    let raw = [];
    if (data == null) return [];

    // Primary: API response shape { message, data: { count, rows: [...] } }
    if (data?.data?.rows && Array.isArray(data.data.rows)) {
      raw = data.data.rows;
    } else if (data?.rows && Array.isArray(data.rows)) {
      raw = data.rows;
    } else if (Array.isArray(data)) {
      raw = data;
    } else if (data?.data?.categories && Array.isArray(data.data.categories)) {
      raw = data.data.categories;
    } else if (data?.data?.data && Array.isArray(data.data.data)) {
      raw = data.data.data;
    } else if (data?.data && Array.isArray(data.data)) {
      raw = data.data;
    } else if (data?.categories && Array.isArray(data.categories)) {
      raw = data.categories;
    } else if (data?.body && Array.isArray(data.body)) {
      raw = data.body;
    } else if (data?.result?.categories && Array.isArray(data.result.categories)) {
      raw = data.result.categories;
    } else if (data?.result && Array.isArray(data.result)) {
      raw = data.result;
    } else if (data && typeof data === 'object') {
      const arrayKeys = ['rows', 'categories', 'responsibilityOptions', 'categoryList', 'categoryOptions', 'list', 'items', 'data', 'options', 'result', 'results'];
      for (const key of arrayKeys) {
        if (Array.isArray(data[key])) {
          raw = data[key];
          break;
        }
      }
      if (raw.length === 0) {
        for (const key of Object.keys(data)) {
          if (Array.isArray(data[key])) {
            raw = data[key];
            break;
          }
        }
      }
      // Backend may return { "Customer": { subCategory: [...] }, "Contractor": {...} }
      if (raw.length === 0 && typeof data === 'object' && !Array.isArray(data)) {
        const entries = Object.entries(data).filter(([k, v]) => v && typeof v === 'object' && (Array.isArray(v.subCategory) || Array.isArray(v.SubCategory) || Array.isArray(v.subCategories)));
        if (entries.length > 0) {
          raw = entries.map(([category, val]) => ({
            category,
            subCategory: val.subCategory ?? val.SubCategory ?? val.subCategories ?? []
          }));
        }
      }
    }

    const options = raw.map(normalizeCategoryItem).filter(Boolean);
    return options;
  };

  // Fetch responsibility options (call with dprLogId when available so backend can return project-specific data)
  const fetchResponsibilityData = useCallback(async () => {
    try {
      const url =  '/downtime-responsibility';
      const response = await axiosConfig.get(url);
      const body = response?.data;

      let options = parseResponsibilityResponse(body);

      // Fallback: try without dprLogId if we got nothing (in case backend only has global list)
      if (options.length === 0 && dprLogId) {
        const fallbackResponse = await axiosConfig.get('/downtime-responsibility');
        options = parseResponsibilityResponse(fallbackResponse?.data);
      }

      setResponsibilityOptions(options);
    } catch (error) {
      console.error('Error fetching downtime responsibilities:', error);
      console.error('Error response:', error?.response?.data ?? error?.message);
      setResponsibilityOptions([]);
    }
  }, [dprLogId]);

  useEffect(() => {
    fetchDowntimeData();
    fetchResponsibilityData();
  }, [fetchDowntimeData, fetchResponsibilityData]); // Re-fetch when shift changes

  // Calculate total downtime from all rows whenever downtimeRows changes
  useEffect(() => {
    const total = downtimeRows.reduce((sum, row) => {
      const duration = parseFloat(row.totalDuration) || 0;
      return sum + duration;
    }, 0);
    setTotalDowntimeToday(roundHours(total));
  }, [downtimeRows]);

  // Real-time validation: Check and update validation errors when downtimeRows change
  useEffect(() => {
    const errors = {};

    downtimeRows.forEach((row) => {
      if (row.responsibilities && row.responsibilities.length > 0) {
        const totalResponsibilityHours = row.responsibilities.reduce(
          (sum, resp) => sum + (parseFloat(resp.hours) || 0),
          0
        );
        const totalHours = parseFloat(row.totalDuration) || 0;
        const roundedResponsibilityHours = roundHours(totalResponsibilityHours);
        const roundedTotalHours = roundHours(totalHours);
        const isValid = Math.abs(roundedResponsibilityHours - roundedTotalHours) < 0.1;

        // Only show error if there's a mismatch AND there are actual responsibility hours entered
        // If validation passes (isValid is true), the error will be cleared automatically
        if (!isValid && totalResponsibilityHours > 0 && totalHours > 0) {
          const missingHours = Math.abs(roundedTotalHours - roundedResponsibilityHours);
          errors[row.id] = `Total responsibility hours (${roundedResponsibilityHours.toFixed(1)}) does not match total downtime hours (${roundedTotalHours.toFixed(1)}). Missing: ${missingHours.toFixed(1)} hrs`;
        }
      }
    });

    setValidationErrors(errors);
  }, [downtimeRows]);

  // Scroll to newly added card
  useEffect(() => {
    if (newCardId && cardRefs.current[newCardId]) {
      setTimeout(() => {
        cardRefs.current[newCardId]?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
        setNewCardId(null); // Reset after scrolling
      }, 100); // Small delay to ensure DOM is updated
    }
  }, [newCardId, downtimeRows]);

  // Helper function to convert categoryTitle to categoryKey (camelCase)
  const convertToCategoryKey = (categoryTitle) => {
    if (!categoryTitle) return 'downtime';
    return categoryTitle
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '')
      .replace(/^[a-z]/, (char) => char.toLowerCase());
  };

  // Helper function to format time to HH:mm
  const formatTime = (date) => {
    if (!date) return '';
    const d = moment(date);
    return d.format('HH:mm');
  };

  const buildDowntimeDetailsPayload = () => {
    // Build downtimeDetails array from downtimeRows
    const downtimeDetails = downtimeRows.map((row) => {
      const originalDetail = row.originalDetail || {};

      // Map responsibilities back to allocations
      const allocations = (row.responsibilities || []).map((resp) => {
        const originalAllocation = resp.originalAllocation || {};
        const allocation = {
          category: resp.category || '',
          subCategory: resp.subCategory || '',
          option: Array.isArray(resp.option) ? resp.option : [],
          hours: resp.hours || 0
        };

        // Include id ONLY if it exists in the original allocation (for existing entries being updated)
        // For new responsibilities added by user, don't include id
        if (originalAllocation && originalAllocation.id) {
          allocation.id = originalAllocation.id;
        }

        return allocation;
      });

      // Build base object
      const detail = {
        categoryTitle: row.reason || '', // categoryTitle from description input
        reason: row.notes || '', // reason from notes field
        startTime: formatTime(row.timeFrom), // startTime from time from picker
        endTime: formatTime(row.timeTo), // endTime from time to picker
        totalHours: row.totalDuration || 0, // totalHours from calculated duration
        remarks: row.notes || '', // remarks from notes field (same as reason)
        detailsKey: 'crceDetails', // hardcoded as requested
        categoryKey: convertToCategoryKey(row.reason), // generate from categoryTitle
        allocations: allocations // Map responsibilities to allocations (empty array if no responsibilities)
      };

      // If it's an existing entry (has originalDetail with id), include id
      if (originalDetail.id) {
        detail.id = originalDetail.id;
        // Also preserve original categoryKey and detailsKey if they exist
        if (originalDetail.categoryKey) {
          detail.categoryKey = originalDetail.categoryKey;
        }
        if (originalDetail.detailsKey) {
          detail.detailsKey = originalDetail.detailsKey;
        }
      }

      return detail;
    });

    return downtimeDetails;
  };

  // Validation function to check if responsibility hours match total downtime hours
  const validateResponsibilityHours = () => {
    const errors = {};
    let hasErrors = false;

    downtimeRows.forEach((row) => {
      if (row.responsibilities && row.responsibilities.length > 0) {
        const totalResponsibilityHours = row.responsibilities.reduce(
          (sum, resp) => sum + (parseFloat(resp.hours) || 0),
          0
        );
        const totalHours = row.totalDuration || 0;
        const roundedResponsibilityHours = roundHours(totalResponsibilityHours);
        const roundedTotalHours = roundHours(totalHours);
        const isValid = Math.abs(roundedResponsibilityHours - roundedTotalHours) < 0.1;

        if (!isValid) {
          const missingHours = Math.abs(roundedTotalHours - roundedResponsibilityHours);
          errors[row.id] = `Total responsibility hours (${roundedResponsibilityHours.toFixed(1)}) does not match total downtime hours (${roundedTotalHours.toFixed(1)}). Missing: ${missingHours.toFixed(1)} hrs`;
          hasErrors = true;
        }
      }
    });

    setValidationErrors(errors);
    return !hasErrors;
  };

  const handleSave = async () => {
    if (!dprLogId) {
      console.error('dprLogId is required to save downtime');
      return;
    }

    // Validate responsibility hours before saving
    if (!validateResponsibilityHours()) {
      console.error('Validation failed: Responsibility hours do not match total downtime hours');
      message.error('Cannot save: Total responsibility hours must match total downtime hours for all entries with responsibilities');
      return;
    }

    setIsSaving(true);
    try {
      // Build downtimeDetails array
      const downtimeDetails = buildDowntimeDetailsPayload();

      // Build payload
      const payload = {
        shift: shift,
        dprDowntime: {
          dprLogId: dprLogId,
          reportNo: reportNo || `DPR-${dprLogId}-D`,
          notes: remarks || '', // notes from remarks textarea
          downtimeDetails: downtimeDetails,
          weldingDowntimeReasons: availableReasons
        }
      };

      console.log('Saving downtime report with payload:', payload);

      // Make PUT request
      await axiosConfig.put('/dpr-downtime', payload);

      // Refetch data to update UI
      await fetchDowntimeData();

      // Clear validation errors on successful save
      setValidationErrors({});

      // Call parent onSave callback if provided
      if (onSave) {
        onSave({
          downtimeRows,
          remarks
        });
      }
    } catch (error) {
      console.error('Error saving downtime report:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddDowntimeEvent = () => {
    const newId = Date.now();
    const newRow = {
      id: newId,
      reason: '',
      notes: '',
      timeFrom: null,
      timeTo: null,
      totalDuration: 0,
      responsibilities: [] // Start with empty array, user can add responsibilities
    };
    setDowntimeRows([...downtimeRows, newRow]);
    setNewCardId(newId); // Set the new card ID to trigger scroll
  };

  const handleAddResponsibility = (downtimeId) => {
    setDowntimeRows(downtimeRows.map(row =>
      row.id === downtimeId
        ? {
          ...row,
          responsibilities: [...(row.responsibilities || []), {
            id: Date.now(),
            category: '',
            subCategory: '',
            option: [],
            hours: 0
          }]
        }
        : row
    ));
  };

  const handleDeleteResponsibility = (downtimeId, responsibilityId) => {
    setDowntimeRows(downtimeRows.map(row =>
      row.id === downtimeId
        ? {
          ...row,
          responsibilities: (row.responsibilities || []).filter(resp => resp.id !== responsibilityId)
        }
        : row
    ));
  };

  const handleResponsibilityChange = (downtimeId, responsibilityId, field, value) => {
    setDowntimeRows(downtimeRows.map(row =>
      row.id === downtimeId
        ? {
          ...row,
          responsibilities: (row.responsibilities || []).map(resp => {
            if (resp.id !== responsibilityId) return resp;

            // Cascading reset logic
            if (field === 'category') {
              return { ...resp, category: value, subCategory: '', option: [] };
            } else if (field === 'subCategory') {
              return { ...resp, subCategory: value, option: [] };
            } else {
              return { ...resp, [field]: value };
            }
          })
        }
        : row
    ));
  };

  // Handle click on delete button - open modal
  const handleDeleteRow = (id) => {
    setRowToDeleteId(id);
    setIsDeleteModalOpen(true);
  };

  // Close delete modal
  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setRowToDeleteId(null);
  };

  // Confirm delete logic
  const handleConfirmDeleteRow = async () => {
    if (!rowToDeleteId) return;

    try {
      // Find the row to delete
      const rowToDelete = downtimeRows.find(row => String(row.id) === String(rowToDeleteId));

      // Remove the row from state
      const updatedRows = downtimeRows.filter(row => {
        if (!row || (row.id === undefined && row.id !== 0)) {
          return true; // Keep rows without IDs (shouldn't happen)
        }
        return String(row.id) !== String(rowToDeleteId);
      });

      setDowntimeRows(updatedRows);

      // Call API immediately with updated data
      if (dprLogId) {
        setIsSaving(true);
        try {
          // Build downtimeDetails array from updated rows (excluding deleted entry)
          const downtimeDetails = updatedRows.map((row) => {
            const originalDetail = row.originalDetail || {};

            // Map responsibilities back to allocations
            const allocations = (row.responsibilities || []).map((resp) => {
              const originalAllocation = resp.originalAllocation || {};
              const allocation = {
                category: resp.category || '',
                subCategory: resp.subCategory || '',
                option: Array.isArray(resp.option) ? resp.option : [],
                hours: parseFloat(resp.hours) || 0
              };

              // Include id ONLY if it exists in the original allocation
              if (originalAllocation && originalAllocation.id) {
                allocation.id = originalAllocation.id;
              }

              return allocation;
            });

            // Build base object
            const detail = {
              categoryTitle: row.reason || '',
              reason: row.notes || '',
              startTime: formatTime(row.timeFrom),
              endTime: formatTime(row.timeTo),
              totalHours: row.totalDuration || 0,
              remarks: row.notes || '',
              detailsKey: 'crceDetails',
              categoryKey: convertToCategoryKey(row.reason),
              allocations: allocations
            };

            // If it's an existing entry (has originalDetail with id), include id
            if (originalDetail.id) {
              detail.id = originalDetail.id;
              // Also preserve original categoryKey and detailsKey if they exist
              if (originalDetail.categoryKey) {
                detail.categoryKey = originalDetail.categoryKey;
              }
              if (originalDetail.detailsKey) {
                detail.detailsKey = originalDetail.detailsKey;
              }
            }

            return detail;
          });

          // Build payload
          const payload = {
            shift: shift,
            dprDowntime: {
              dprLogId: dprLogId,
              reportNo: reportNo || `DPR-${dprLogId}-D`,
              notes: remarks || '',
              downtimeDetails: downtimeDetails,
              weldingDowntimeReasons: availableReasons
            }
          };

          await axiosConfig.put('/dpr-downtime', payload);
          message.success('Downtime entry deleted successfully');

          // Refetch data to update UI
          await fetchDowntimeData();

          // Clear validation errors on successful deletion
          setValidationErrors({});
        } catch (error) {
          console.error('Error deleting downtime entry:', error);
          message.error('Failed to delete downtime entry');
          // Revert state on error
          // Note: Ideally we should fetch data again, but for now we rely on UI not updating if API fails or optimistically updating.
          // Since we already updated state optimistically, we might want to revert it but complex without keeping previous state copy.
          await fetchDowntimeData(); // Refetch to restore correct state
        } finally {
          setIsSaving(false);
        }
      }
    } catch (error) {
      console.error('Error in delete confirmation:', error);
    } finally {
      handleCloseDeleteModal();
    }
  };

  // Handle adding a new reason (called from CustomReasonSelect with reason text)
  const handleAddNewReason = (rowId, reasonText) => {
    if (!reasonText || !reasonText.trim()) return;

    const newReason = {
      code: reasonText.toUpperCase().replace(/[^A-Z0-9]/g, '_'),
      label: reasonText.trim(),
      description: reasonText.trim()
    };

    setAvailableReasons((prev) => [...prev, newReason]);
    handleRowChange(rowId, 'reason', newReason.label);
  };

  const handleRowChange = (id, field, value) => {
    setDowntimeRows(downtimeRows.map(row => {
      if (row.id === id) {
        const updatedRow = { ...row, [field]: value };
        // Calculate total duration if timeFrom or timeTo changes
        if (field === 'timeFrom' || field === 'timeTo') {
          if (updatedRow.timeFrom && updatedRow.timeTo) {
            const from = new Date(updatedRow.timeFrom);
            const to = new Date(updatedRow.timeTo);

            // Calculate duration in milliseconds then convert to hours
            let durationMs = to.getTime() - from.getTime();

            // If duration is negative, it means we crossed midnight (e.g., PM to AM)
            // Add 24 hours to account for the next day
            if (durationMs < 0) {
              durationMs = durationMs + (24 * 60 * 60 * 1000);
            }

            // Convert from milliseconds to hours
            const duration = durationMs / (1000 * 60 * 60);

            // Ensure duration is not negative (shouldn't happen after the above check, but just in case)
            updatedRow.totalDuration = duration >= 0 ? roundHours(duration) : 0;
          } else {
            updatedRow.totalDuration = 0;
          }
        }

        // Validation will be automatically updated by useEffect
        return updatedRow;
      }
      return row;
    }));
  };

  // Calculate total in minutes from totalDowntimeToday (which is in hours)
  const totalMinutes = totalDowntimeToday * 60;

  return (
    <div style={{ position: 'relative' }}>
      {isLoading && (
        <div style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(255,255,255,0.7)',
          zIndex: 1000
        }}>
          <Spinner variant="inline" size="medium" />
        </div>
      )}

      {/* Main Container */}
      <div style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '12px',
        padding: '32px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '24px'
      }}>
        {/* Header Section */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
            <div style={{
              backgroundColor: '#FFF7ED', // Light orange
              padding: '10px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#EA580C' // Orange
            }}>
              <Clock size={24} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#0F172A', textAlign: 'left' }}>
                  Downtime Log
                </h3>
              </div>
              <p style={{ margin: 0, fontSize: '14px', color: '#64748B', textAlign: 'left' }}>
                Record operational delays and assign responsibility.
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Checkbox
              label="Skip Downtime"
              checked={skipDowntime}
              onChange={handleSkipDowntimeChange}
              borderColor="#00C9A7"
            />
            <Button
              label="Add Downtime Event"
              variant="primary"
              size="small"
              icon={Plus}
              onClick={handleAddDowntimeEvent}
              style={{ backgroundColor: '#EA580C', borderColor: '#EA580C' }}
            />
          </div>
        </div>

        {/* Downtime Events Table Section */}

        {/* Table Headers */}
        <div style={{ marginBottom: '16px' }}>
          <Header
            items={[
              { label: 'REASON / DESCRIPTION', width: '20%' },
              { label: 'TIME FROM', width: '25%', align: 'right' },
              { label: 'TIME TO', width: '25%', align: 'right' },
              { label: 'TOTAL DURATION', width: '22%', align: 'center' },
              { label: 'ACTION', width: '10%', align: 'center' }
            ]}
            size="small"
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {downtimeRows.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '48px 24px',
              backgroundColor: '#FAFAFA',
              border: '2px dashed #E5E7EB',
              borderRadius: '8px'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                backgroundColor: '#F3F4F6',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px auto'
              }}>
                <AlertTriangle size={24} color="#9CA3AF" />
              </div>
              <div style={{ color: '#6B7280', fontSize: '15px', fontWeight: 500, marginBottom: '8px' }}>
                No downtime events recorded.
              </div>
              <div
                onClick={handleAddDowntimeEvent}
                style={{
                  color: '#F97316',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <Plus size={16} /> Report a delay
              </div>
            </div>
          ) : (
            downtimeRows.map((row) => (
              <div
                key={row.id}
                ref={(el) => {
                  if (el) {
                    cardRefs.current[row.id] = el;
                  }
                }}
                style={{
                  padding: '24px',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '24px'
                }}
              >
                {/* Main Downtime Event Fields */}
                <div>
                  <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'flex-start' }}>
                      <CustomReasonSelect
                        value={row.reason || ''}
                        options={availableReasons}
                        onChange={(val) => handleRowChange(row.id, 'reason', val)}
                        onAddReason={(reasonText) => handleAddNewReason(row.id, reasonText)}
                        placeholder="Select Reason..."
                      />
                    </div>
                    <div style={{ width: '200px', display: 'flex', justifyContent: 'flex-start' }}>
                      <input
                        type="time"
                        value={formatTimeForInput(row.timeFrom)}
                        onChange={(e) => handleRowChange(row.id, 'timeFrom', parseTimeFromInput(e.target.value))}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          fontSize: '14px',
                          border: '1px solid #E2E8F0',
                          borderRadius: '8px',
                          backgroundColor: '#FFFFFF',
                          color: '#000000',
                          outline: 'none',
                          cursor: 'pointer',
                          fontFamily: 'inherit'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#4F46E5'}
                        onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                      />
                    </div>
                    <div style={{ width: '200px', display: 'flex', justifyContent: 'flex-start' }}>
                      <input
                        type="time"
                        value={formatTimeForInput(row.timeTo)}
                        onChange={(e) => handleRowChange(row.id, 'timeTo', parseTimeFromInput(e.target.value))}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          fontSize: '14px',
                          border: '1px solid #E2E8F0',
                          borderRadius: '8px',
                          backgroundColor: '#FFFFFF',
                          color: '#000000',
                          outline: 'none',
                          cursor: 'pointer',
                          fontFamily: 'inherit'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#4F46E5'}
                        onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                      />
                    </div>
                    <div style={{ width: '200px', display: 'flex', justifyContent: 'flex-start' }}>
                      <div style={{
                        backgroundColor: '#E0F2FE',
                        borderRadius: '8px',
                        padding: '8px 16px',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        height: '40px',
                        minWidth: '140px',
                        width: '100%',
                        boxSizing: 'border-box'
                      }}>
                        <span style={{ color: '#0F172A', fontSize: '15px', fontWeight: 700 }}>
                          {roundHours(row.totalDuration).toFixed(1)}
                        </span>
                        <span style={{ color: '#64748B', fontSize: '12px', fontWeight: 600 }}>
                          HRS
                        </span>
                      </div>
                    </div>
                    <div style={{ width: '40px', display: 'flex', justifyContent: 'center' }}>
                      <button
                        onClick={() => handleDeleteRow(row.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#D1D5DB',
                          padding: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.color = '#EF4444'}
                        onMouseLeave={(e) => e.currentTarget.style.color = '#D1D5DB'}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>

                  {/* Add Notes (Subtle Input) */}
                  <div style={{ marginTop: '12px' }}>
                    <input
                      type="text"
                      value={row.notes || ''}
                      onChange={(e) => handleRowChange(row.id, 'notes', e.target.value)}
                      placeholder="Add notes..."
                      style={{
                        width: '100%',
                        border: 'none',
                        borderBottom: '1px solid #EEF2FF',
                        padding: '8px 0',
                        fontSize: '13px',
                        color: '#4B5563',
                        outline: 'none',
                        background: 'transparent'
                      }}
                    />
                  </div>
                </div>

                {/* Responsibility Split Section */}
                <div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '12px',
                    gap: '8px'
                  }}>
                    <div style={{ transform: 'rotate(90deg)', display: 'flex' }}>
                      <ArrowRightOutlined style={{ color: '#CBD5E1', fontSize: '16px' }} />
                    </div>
                    <div style={{ color: '#94A3B8', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      RESPONSIBILITY SPLIT
                    </div>
                  </div>

                  <div style={{
                    padding: '20px',
                    backgroundColor: '#F8FAFC',
                    borderRadius: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px'
                  }}>
                    {/* Validation Error Message */}
                    {(() => {
                      const totalResponsibilityHours = (row.responsibilities || []).reduce((sum, resp) => sum + (parseFloat(resp.hours) || 0), 0);
                      const totalHours = row.totalDuration || 0;
                      const roundedResponsibilityHours = roundHours(totalResponsibilityHours);
                      const roundedTotalHours = roundHours(totalHours);
                      const isValid = Math.abs(roundedResponsibilityHours - roundedTotalHours) < 0.1;

                      if (!isValid && totalResponsibilityHours > 0) {
                        const missingHours = Math.abs(roundedTotalHours - roundedResponsibilityHours);
                        return (
                          <div style={{
                            color: '#B91C1C',
                            fontSize: '12px',
                            marginBottom: '12px',
                            padding: '8px 12px',
                            backgroundColor: '#FECACA',
                            borderRadius: '6px',
                            border: '1px solid #FCA5A5',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <AlertTriangle size={14} />
                            <span>Total responsibility hours ({roundedResponsibilityHours.toFixed(1)}) does not match total downtime hours ({roundedTotalHours.toFixed(1)}). Missing: {missingHours.toFixed(1)} hrs</span>
                          </div>
                        );
                      }
                      return null;
                    })()}

                    {(row.responsibilities && row.responsibilities.length > 0) ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {(row.responsibilities || []).map((resp) => (
                          <div key={resp.id} style={{
                            display: 'flex',
                            width: '100%',
                            gap: '12px',
                            alignItems: 'flex-end',
                            flexWrap: 'nowrap',
                            padding: '12px',
                            backgroundColor: '#FFFFFF',
                            borderRadius: '8px',
                            border: '1px solid #E5E7EB'
                          }}>
                            {/* Category Select */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <label style={{ display: 'block', fontSize: '11px', fontWeight: 500, color: '#64748B', marginBottom: '4px', textAlign: 'left' }}>
                                Category
                              </label>
                              <CustomSingleSelect
                                value={resp.category || ''}
                                options={responsibilityOptions
                                  .filter((cat) => (cat.category || cat.name || cat.categoryName || '').trim())
                                  .map((cat) => cat.category || cat.name || cat.categoryName || '')}
                                onChange={(val) => handleResponsibilityChange(row.id, resp.id, 'category', val)}
                                placeholder="Select Category..."
                              />
                            </div>

                            {/* Subcategory Select */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <label style={{ display: 'block', fontSize: '11px', fontWeight: 500, color: '#64748B', marginBottom: '4px', textAlign: 'left' }}>
                                Subcategory
                              </label>
                              <CustomSingleSelect
                                value={resp.subCategory || ''}
                                options={(() => {
                                  const selectedCat = responsibilityOptions.find(c => (c.category || c.name || c.categoryName) === resp.category);
                                  const subCategories = selectedCat?.subCategory || [];
                                  return subCategories.map((sub) => (typeof sub === 'string' ? sub : (sub.name || sub.subCategoryName || sub)));
                                })()}
                                onChange={(val) => handleResponsibilityChange(row.id, resp.id, 'subCategory', val)}
                                placeholder="Select Subcategory..."
                                disabled={!resp.category}
                              />
                            </div>

                            {/* Option Multi-Select - more width so multiple tags don't overflow */}
                            <div style={{ flex: 3, minWidth: 0 }}>
                              <label style={{ display: 'block', fontSize: '11px', fontWeight: 500, color: '#64748B', marginBottom: '4px', textAlign: 'left' }}>
                                Option
                              </label>
                              <CustomOptionMultiSelect
                                value={resp.option || []}
                                options={(() => {
                                  const selectedCat = responsibilityOptions.find(c => (c.category || c.name || c.categoryName) === resp.category);
                                  const subCategories = selectedCat?.subCategory || [];
                                  const selectedSub = subCategories.find(s => (s.name || s.subCategoryName || s) === resp.subCategory);
                                  return selectedSub?.Options || selectedSub?.options || [];
                                })()}
                                onChange={(values) => handleResponsibilityChange(row.id, resp.id, 'option', values)}
                                placeholder="Select Options..."
                                disabled={!resp.category || !resp.subCategory}
                              />
                            </div>

                            {/* Hours Input + Delete (same line and height as selects) */}
                            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', flexShrink: 0 }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                border: '1px solid #E5E7EB',
                                borderRadius: '6px',
                                backgroundColor: '#FFFFFF',
                                padding: '0 8px',
                                height: '38px',
                                boxSizing: 'border-box'
                              }}>
                                <input
                                  type="number"
                                  min={0}
                                  step={0.1}
                                  value={resp.hours}
                                  onChange={(e) => handleResponsibilityChange(row.id, resp.id, 'hours', e.target.value === '' ? 0 : parseFloat(e.target.value) || 0)}
                                  style={{
                                    width: '40px',
                                    border: 'none',
                                    padding: '0',
                                    textAlign: 'right',
                                    fontSize: '13px',
                                    color: '#1F2937',
                                    outline: 'none',
                                    backgroundColor: 'transparent'
                                  }}
                                />
                                <span style={{ color: '#94A3B8', fontSize: '12px', fontWeight: 500, marginLeft: '4px' }}>H</span>
                              </div>

                              {/* Delete Button - same height as selects */}
                              <div style={{ height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <button
                                  onClick={() => handleDeleteResponsibility(row.id, resp.id)}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: '#D1D5DB',
                                    padding: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.color = '#EF4444'}
                                  onMouseLeave={(e) => e.currentTarget.style.color = '#D1D5DB'}
                                >
                                  <Trash2 size={18} />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{
                        color: '#9CA3AF',
                        fontSize: '13px',
                        textAlign: 'center',
                        fontStyle: 'italic',
                        padding: '12px'
                      }}>
                        No responsibilities added yet.
                      </div>
                    )}

                    <div>
                      <div
                        onClick={() => handleAddResponsibility(row.id)}
                        style={{
                          color: '#F59E0B',
                          fontSize: '13px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          marginTop: '8px'
                        }}
                      >
                        <span style={{
                          display: 'flex',
                          alignItems: 'center',
                          transform: 'rotate(90deg) scaleX(-1)' // Rotate and flip to match the corner arrow style
                        }}>
                          <ArrowRightOutlined style={{ fontSize: '14px' }} />
                        </span>
                        Add Another Responsibility
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            ))
          )}
        </div>


        {/* Downtime Notes / Remarks Section */}
        <CommentBox
          header="Downtime Notes / Remarks"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Enter any additional remarks regarding delays or downtime..."
        />

        {/* Global Validation Error Message */}
        {
          Object.keys(validationErrors).length > 0 && (
            <div style={{
              color: '#ff4d4f',
              fontSize: '14px',
              marginTop: '16px',
              padding: '12px',
              backgroundColor: 'rgba(255, 77, 79, 0.1)',
              borderRadius: '4px',
              border: '1px solid rgba(255, 77, 79, 0.3)',
              marginBottom: '16px'
            }}>
              <div style={{ fontWeight: 500, marginBottom: '8px' }}>⚠️ Validation Errors:</div>
              {Object.entries(validationErrors).map(([rowId, error]) => (
                <div key={rowId} style={{ marginBottom: '4px' }}>
                  {error}
                </div>
              ))}
            </div>
          )
        }

        {/* Save Button */}
        <div style={{
          position: 'sticky',
          bottom: '24px',
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: '24px',
          paddingTop: '24px',
          zIndex: 10,
          backgroundColor: '#FFFFFF', // Ensure opaque background for sticky footer
        }}>
          <Button
            label="Save Downtime Report"
            variant="secondary"
            onClick={handleSave}
            disabled={isSaving || Object.keys(validationErrors).length > 0}
            style={{ paddingLeft: '32px', paddingRight: '32px' }}
          />
        </div>

        <ConfirmationModal
          title="Delete Downtime Entry"
          message="Are you sure you want to delete this downtime entry? This action cannot be undone."
          confirmText="Yes, Delete"
          cancelText="Cancel"
          onConfirm={handleConfirmDeleteRow}
          onCancel={handleCloseDeleteModal}
          isOpen={isDeleteModalOpen}
          size="medium"
          confirmButtonProps={{
            style: {
              backgroundColor: '#FF3B30',
              borderColor: '#FF3B30'
            }
          }}
        />
        <ConfirmationModal
          title="Skip Downtime"
          message={`Are you sure you want to skip the Downtime log for ${shift === 'night' ? 'night' : 'day'} shift?`}
          confirmText="Confirm"
          cancelText="Cancel"
          isOpen={isSkipConfirmOpen}
          onConfirm={handleConfirmSkipDowntime}
          onCancel={() => setIsSkipConfirmOpen(false)}
          size="medium"
        />
      </div>
    </div>
  );
};

export default DowntimeLog;

